import type { AchievementRecord } from './api';

/**
 * Pure state machine for the drag-rehome interaction — the brief's explicit
 * ask for THIS tab (optimistic UI + server-confirm + error rollback), which is
 * a deliberate departure from apps/web's own drag handler (Corpus.tsx:404-484):
 * that one never moves a card locally, it only fires the PUT and waits for a
 * full refresh. See apps/throughline/src/lib/api.ts's rehomeAchievement doc
 * comment for the full rationale. Kept framework-free (no React) so the whole
 * lifecycle — snapshot, optimistic move, confirm, rollback — is unit-testable
 * without mounting a component or a DnD context.
 *
 * Lifecycle:
 *   idle -> pending (optimistic move applied, snapshot kept)
 *   pending -> idle (confirmed: server accepted, snapshot discarded)
 *   pending -> idle (rolled back: server rejected, snapshot restored)
 * Only one card may be mid-rehome at a time (matches the UX — you drag one
 * card at once); starting a new drag while one is pending is a caller error
 * this module does not need to guard, since the UI disables further drags
 * while a rehome is pending (see CorpusCanvas.tsx).
 */

export interface DragRehomeState {
  /** The card currently mid-rehome, or null when idle. */
  pendingCardId: string | null;
  /** The FULL achievements array from just before the optimistic move, so a
   *  failure can restore it byte-for-byte rather than trying to reason about
   *  a partial undo. Null when idle. */
  snapshot: AchievementRecord[] | null;
}

export const INITIAL_DRAG_REHOME_STATE: DragRehomeState = {
  pendingCardId: null,
  snapshot: null,
};

/** Apply the optimistic move: relocate `cardId` to `targetRoleId` (null =
 *  unattached) in a NEW array (never mutate the input), and return both the
 *  next achievements list and the state needed to roll back. Snapshot is the
 *  ORIGINAL array reference passed in — cheap, since arrays here are already
 *  treated as immutable by every caller (React state). */
export function applyOptimisticRehome(
  achievements: AchievementRecord[],
  cardId: string,
  targetRoleId: string | null,
  targetRoleEventId: string | null,
): { next: AchievementRecord[]; state: DragRehomeState } {
  const next = achievements.map((a) =>
    a.id === cardId ? { ...a, roleId: targetRoleId, roleEventId: targetRoleEventId } : a,
  );
  return {
    next,
    state: { pendingCardId: cardId, snapshot: achievements },
  };
}

/** The server confirmed — drop the snapshot (the optimistic move stands; a
 *  caller that wants the fully server-reconciled truth should still re-fetch,
 *  since a supersede mints a NEW id for the moved card that this optimistic
 *  patch does not know about — see CorpusCanvas.tsx's confirm handler). */
export function confirmRehome(): DragRehomeState {
  return INITIAL_DRAG_REHOME_STATE;
}

/** The server rejected (or the request threw) — restore the pre-drag snapshot
 *  so the card visually returns to where it actually lives. Returns null for
 *  `restored` if called with no pending snapshot (nothing to roll back; a
 *  caller bug, not a user-facing error) so the caller can decide how to log it
 *  without this module needing to throw. */
export function rollbackRehome(state: DragRehomeState): {
  restored: AchievementRecord[] | null;
  state: DragRehomeState;
} {
  return { restored: state.snapshot, state: INITIAL_DRAG_REHOME_STATE };
}

/** Whether a card is currently mid-rehome (drag disabled for others while
 *  this is true, matching the "one card at a time" UX). */
export function isRehomePending(state: DragRehomeState): boolean {
  return state.pendingCardId !== null;
}
